import React, { useState, useEffect, useMemo } from 'react';
import { getAllFormResponses } from '../services/supabaseClient';
import { FiMapPin, FiTrendingUp, FiDollarSign, FiUsers, FiBarChart2, FiPackage, FiSearch, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import municipios from '../data/municipios.json';

const MunicipioStats = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [responses, setResponses] = useState([]);
  const [selectedMunicipio, setSelectedMunicipio] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    economico: true,
    programas: true,
    infraestrutura: true
  });

  useEffect(() => {
    const fetchResponses = async () => {
      setLoading(true);
      try {
        const { success, data, error } = await getAllFormResponses();
        
        if (!success) {
          throw new Error(error || 'Erro ao buscar respostas');
        }
        
        // Filtrar apenas respostas enviadas
        const respostasEnviadas = data.filter(r => r.status === 'submitted');
        setResponses(respostasEnviadas || []);
      } catch (err) {
        console.error('Error fetching responses:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchResponses();
  }, []);

  // Análise por município
  const municipioData = useMemo(() => {
    if (responses.length === 0) return [];

    const municipiosMap = {};

    responses.forEach(response => {
      const nomeMunicipio = response.responses?.nome_municipio;
      if (!nomeMunicipio) return;

      if (!municipiosMap[nomeMunicipio]) {
        municipiosMap[nomeMunicipio] = {
          nome: nomeMunicipio,
          codigoIbge: 'N/A',
          totalRespostas: 0,
          populacao: 'N/A',
          area: 'N/A',
          idhm: 'N/A',
          gini: 'N/A',
          pibMunicipal: 'N/A',
          participacaoPib: 'N/A',
          taxaDesemprego: 'N/A',
          rendaPerCapita: 'N/A',
          taxaPobreza: 'N/A',
          setoresEconomicos: new Set(),
          programas: [],
          temApl: false,
          aplDescricao: null,
          infraestruturaApoio: [],
          desafiosSocioeconomicos: [],
          exportacoesImportacoes: 'N/A'
        };
      }

      const mun = municipiosMap[nomeMunicipio];
      mun.totalRespostas++;

      // Função auxiliar para verificar se um valor é válido (não nulo, não vazio, não zero)
      const isValidValue = (value) => {
        if (value === null || value === undefined || value === '' || value === 'N/A') {
          return false;
        }
        // Se for número, verificar se não é zero
        const numValue = parseFloat(value);
        if (!isNaN(numValue) && numValue === 0) {
          return false;
        }
        return true;
      };

      // Atualizar campos únicos apenas se o valor atual for inválido OU se o novo valor for válido
      if (mun.codigoIbge === 'N/A' && isValidValue(response.responses?.codigo_ibge)) {
        mun.codigoIbge = response.responses.codigo_ibge;
      }
      if (mun.populacao === 'N/A' && isValidValue(response.responses?.populacao)) {
        mun.populacao = response.responses.populacao;
      }
      if (mun.area === 'N/A' && isValidValue(response.responses?.area)) {
        mun.area = response.responses.area;
      }
      if (mun.idhm === 'N/A' && isValidValue(response.responses?.idhm)) {
        mun.idhm = response.responses.idhm;
      }
      if (mun.gini === 'N/A' && isValidValue(response.responses?.gini)) {
        mun.gini = response.responses.gini;
      }
      if (mun.pibMunicipal === 'N/A' && isValidValue(response.responses?.pib_municipal)) {
        mun.pibMunicipal = response.responses.pib_municipal;
      }
      if (mun.participacaoPib === 'N/A' && isValidValue(response.responses?.participacao_pib)) {
        mun.participacaoPib = response.responses.participacao_pib;
      }
      if (mun.taxaDesemprego === 'N/A' && isValidValue(response.responses?.taxa_desemprego)) {
        mun.taxaDesemprego = response.responses.taxa_desemprego;
      }
      if (mun.rendaPerCapita === 'N/A' && isValidValue(response.responses?.renda_per_capita)) {
        mun.rendaPerCapita = response.responses.renda_per_capita;
      }
      if (mun.taxaPobreza === 'N/A' && isValidValue(response.responses?.taxa_pobreza)) {
        mun.taxaPobreza = response.responses.taxa_pobreza;
      }
      if (mun.exportacoesImportacoes === 'N/A' && isValidValue(response.responses?.exportacoes_importacoes)) {
        mun.exportacoesImportacoes = response.responses.exportacoes_importacoes;
      }

      // Setores econômicos
      if (response.responses?.setores_economicos) {
        const setores = Array.isArray(response.responses.setores_economicos)
          ? response.responses.setores_economicos
          : [response.responses.setores_economicos];
        setores.forEach(setor => {
          if (setor && setor !== '') mun.setoresEconomicos.add(setor);
        });
      }

      // Programas
      for (let i = 1; i <= 6; i++) {
        const programaNome = response.responses?.[`programa_nome_${i}`];
        const programaFoco = response.responses?.[`programa_foco_${i}`];
        if (programaNome) {
          mun.programas.push({
            nome: programaNome,
            foco: programaFoco || 'N/A'
          });
        }
      }

      // APL
      if (response.responses?.presenca_apl === 'Sim' || response.responses?.presenca_apl === 'sim') {
        mun.temApl = true;
        if (response.responses?.descricao_apl) {
          mun.aplDescricao = response.responses.descricao_apl;
        }
      }

      // Infraestrutura
      if (response.responses?.infraestrutura_apoio) {
        const infra = Array.isArray(response.responses.infraestrutura_apoio)
          ? response.responses.infraestrutura_apoio
          : [response.responses.infraestrutura_apoio];
        mun.infraestruturaApoio.push(...infra.filter(i => i && i !== ''));
      }

      // Desafios
      if (response.responses?.desafios_socioeconomicos) {
        mun.desafiosSocioeconomicos.push(response.responses.desafios_socioeconomicos);
      }
    });

    // Converter Set para Array
    Object.values(municipiosMap).forEach(mun => {
      mun.setoresEconomicos = Array.from(mun.setoresEconomicos);
      mun.infraestruturaApoio = [...new Set(mun.infraestruturaApoio)];
    });

    return Object.values(municipiosMap).sort((a, b) => 
      a.nome.localeCompare(b.nome)
    );
  }, [responses]);

  // Filtrar municípios por busca
  const filteredMunicipios = useMemo(() => {
    if (!searchTerm) return municipioData;
    return municipioData.filter(m => 
      m.nome.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [municipioData, searchTerm]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
        <p>Erro ao carregar os dados: {error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Estatísticas por Município</h1>
        <p className="mt-2 text-gray-600">
          Análise detalhada das informações socioeconômicas e de desenvolvimento produtivo por município da RIDE-DF.
        </p>
      </div>

      {/* Resumo Geral */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <div className="bg-primary-50 overflow-hidden rounded-lg shadow">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FiMapPin className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Municípios com Dados
                </dt>
                <dd className="flex items-baseline">
                  <div className="text-2xl font-semibold text-gray-900">
                    {municipioData.length}
                  </div>
                </dd>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-green-50 overflow-hidden rounded-lg shadow">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FiPackage className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Municípios com APLs
                </dt>
                <dd className="flex items-baseline">
                  <div className="text-2xl font-semibold text-gray-900">
                    {municipioData.filter(m => m.temApl).length}
                  </div>
                </dd>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 overflow-hidden rounded-lg shadow">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FiBarChart2 className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Total de Programas
                </dt>
                <dd className="flex items-baseline">
                  <div className="text-2xl font-semibold text-gray-900">
                    {municipioData.reduce((acc, m) => acc + m.programas.length, 0)}
                  </div>
                </dd>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-purple-50 overflow-hidden rounded-lg shadow">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FiTrendingUp className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Setores Identificados
                </dt>
                <dd className="flex items-baseline">
                  <div className="text-2xl font-semibold text-gray-900">
                    {[...new Set(municipioData.flatMap(m => m.setoresEconomicos))].length}
                  </div>
                </dd>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Busca e Lista de Municípios */}
      <div className="bg-white shadow rounded-lg mb-8">
        <div className="px-4 py-5 sm:p-6">
          <div className="mb-4">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
              Buscar Município
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Digite o nome do município..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredMunicipios.map((municipio) => (
              <button
                key={municipio.nome}
                onClick={() => setSelectedMunicipio(municipio)}
                className={`text-left p-4 border rounded-lg transition-all hover:shadow-md ${
                  selectedMunicipio?.nome === municipio.nome
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-primary-300'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-gray-900">{municipio.nome}</h3>
                    <p className="text-xs text-gray-500 mt-1">
                      {municipio.totalRespostas} resposta(s) • IBGE: {municipio.codigoIbge}
                    </p>
                  </div>
                  <FiMapPin className="h-5 w-5 text-primary-600 flex-shrink-0 ml-2" />
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {municipio.temApl && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      APL
                    </span>
                  )}
                  {municipio.programas.length > 0 && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      {municipio.programas.length} programa(s)
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>

          {filteredMunicipios.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Nenhum município encontrado com o termo "{searchTerm}"
            </div>
          )}
        </div>
      </div>

      {/* Detalhes do Município Selecionado */}
      {selectedMunicipio && (
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{selectedMunicipio.nome}</h2>
                <p className="text-sm text-gray-500 mt-1">Código IBGE: {selectedMunicipio.codigoIbge}</p>
              </div>
              <button
                onClick={() => setSelectedMunicipio(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Fechar</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Indicadores Socioeconômicos */}
            <div className="mb-6">
              <button
                onClick={() => toggleSection('economico')}
                className="flex items-center justify-between w-full text-left mb-4"
              >
                <div className="flex items-center">
                  <FiDollarSign className="h-5 w-5 text-primary-600 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-900">Indicadores Socioeconômicos</h3>
                </div>
                {expandedSections.economico ? (
                  <FiChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <FiChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </button>
              
              {expandedSections.economico && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pl-7">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <dt className="text-sm font-medium text-gray-500">População</dt>
                    <dd className="mt-1 text-lg font-semibold text-gray-900">{selectedMunicipio.populacao}</dd>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <dt className="text-sm font-medium text-gray-500">Área Territorial</dt>
                    <dd className="mt-1 text-lg font-semibold text-gray-900">{selectedMunicipio.area} km²</dd>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <dt className="text-sm font-medium text-gray-500">IDH Municipal</dt>
                    <dd className="mt-1 text-lg font-semibold text-gray-900">{selectedMunicipio.idhm}</dd>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <dt className="text-sm font-medium text-gray-500">Índice de Gini</dt>
                    <dd className="mt-1 text-lg font-semibold text-gray-900">{selectedMunicipio.gini}</dd>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <dt className="text-sm font-medium text-gray-500">PIB Municipal</dt>
                    <dd className="mt-1 text-lg font-semibold text-gray-900">{selectedMunicipio.pibMunicipal}</dd>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <dt className="text-sm font-medium text-gray-500">Participação no PIB RIDE</dt>
                    <dd className="mt-1 text-lg font-semibold text-gray-900">{selectedMunicipio.participacaoPib}%</dd>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <dt className="text-sm font-medium text-gray-500">Taxa de Desemprego</dt>
                    <dd className="mt-1 text-lg font-semibold text-gray-900">{selectedMunicipio.taxaDesemprego}%</dd>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <dt className="text-sm font-medium text-gray-500">Renda Per Capita</dt>
                    <dd className="mt-1 text-lg font-semibold text-gray-900">R$ {selectedMunicipio.rendaPerCapita}</dd>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <dt className="text-sm font-medium text-gray-500">Taxa de Pobreza</dt>
                    <dd className="mt-1 text-lg font-semibold text-gray-900">{selectedMunicipio.taxaPobreza}%</dd>
                  </div>
                </div>
              )}
            </div>

            {/* Setores Econômicos e Desenvolvimento */}
            <div className="mb-6">
              <div className="flex items-center mb-4">
                <FiTrendingUp className="h-5 w-5 text-green-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Setores Econômicos Predominantes</h3>
              </div>
              <div className="pl-7">
                {selectedMunicipio.setoresEconomicos.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {selectedMunicipio.setoresEconomicos.map((setor, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800"
                      >
                        {setor}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Nenhum setor econômico informado</p>
                )}
              </div>
            </div>

            {/* Programas e Políticas Públicas */}
            <div className="mb-6">
              <button
                onClick={() => toggleSection('programas')}
                className="flex items-center justify-between w-full text-left mb-4"
              >
                <div className="flex items-center">
                  <FiBarChart2 className="h-5 w-5 text-blue-600 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-900">Programas e Políticas Públicas</h3>
                </div>
                {expandedSections.programas ? (
                  <FiChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <FiChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </button>
              
              {expandedSections.programas && (
                <div className="pl-7">
                  {selectedMunicipio.programas.length > 0 ? (
                    <div className="space-y-3">
                      {selectedMunicipio.programas.map((programa, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <h4 className="font-medium text-gray-900">{programa.nome}</h4>
                          <p className="text-sm text-gray-600 mt-1">Foco: {programa.foco}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Nenhum programa informado</p>
                  )}

                  {/* APL */}
                  <div className="mt-6">
                    <h4 className="font-medium text-gray-900 mb-2">Arranjo Produtivo Local (APL)</h4>
                    {selectedMunicipio.temApl ? (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center mb-2">
                          <FiPackage className="h-5 w-5 text-green-600 mr-2" />
                          <span className="font-medium text-green-900">APL Identificado</span>
                        </div>
                        {selectedMunicipio.aplDescricao && (
                          <p className="text-sm text-gray-700 mt-2">{selectedMunicipio.aplDescricao}</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Nenhum APL identificado</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Infraestrutura */}
            <div className="mb-6">
              <button
                onClick={() => toggleSection('infraestrutura')}
                className="flex items-center justify-between w-full text-left mb-4"
              >
                <div className="flex items-center">
                  <FiUsers className="h-5 w-5 text-purple-600 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-900">Infraestrutura de Apoio</h3>
                </div>
                {expandedSections.infraestrutura ? (
                  <FiChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <FiChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </button>
              
              {expandedSections.infraestrutura && (
                <div className="pl-7">
                  {selectedMunicipio.infraestruturaApoio.length > 0 ? (
                    <ul className="list-disc list-inside space-y-1">
                      {selectedMunicipio.infraestruturaApoio.map((infra, index) => (
                        <li key={index} className="text-sm text-gray-700">{infra}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500">Nenhuma infraestrutura informada</p>
                  )}
                </div>
              )}
            </div>

            {/* Desafios Socioeconômicos */}
            {selectedMunicipio.desafiosSocioeconomicos.length > 0 && (
              <div>
                <div className="flex items-center mb-4">
                  <FiBarChart2 className="h-5 w-5 text-orange-600 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-900">Desafios Socioeconômicos</h3>
                </div>
                <div className="pl-7">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    {selectedMunicipio.desafiosSocioeconomicos.map((desafio, index) => (
                      <p key={index} className="text-sm text-gray-700 mb-2 last:mb-0">{desafio}</p>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MunicipioStats;
